/**
 * Centralized Salesforce metadata extension to icon type mapping.
 * Add one entry to EXTENSION_TO_ICON_TYPE to extend file manager icons.
 * Default fallback: xml (most Salesforce files are XML).
 */

export type FileIconType =
  | 'apex'
  | 'trigger'
  | 'visualforce'
  | 'lwc'
  | 'aura'
  | 'xml'
  | 'json'
  | 'css'
  | 'html'
  | 'javascript'
  | 'certificate'
  | 'default';

export const DEFAULT_ICON_TYPE: FileIconType = 'xml';

/**
 * Extension (lowercase) to icon type map.
 * To add a new file type: add one entry here.
 */
export const EXTENSION_TO_ICON_TYPE: Record<string, FileIconType> = {
  cls: 'apex',
  apex: 'apex',
  trigger: 'trigger',
  component: 'visualforce',
  page: 'visualforce',
  js: 'javascript',
  design: 'javascript',
  html: 'html',
  scf: 'html',
  json: 'json',
  css: 'css',
  crt: 'certificate',
  xml: 'xml',
  // XML-based Salesforce metadata
  notifications: 'xml',
  appmenu: 'xml',
  assignmentrules: 'xml',
  audience: 'xml',
  autoresponserules: 'xml',
  cleandataservice: 'xml',
  community: 'xml',
  corswhitelistorigin: 'xml',
  app: 'xml',
  labels: 'xml',
  object: 'xml',
  tab: 'xml',
  duplicaterule: 'xml',
  escalationrules: 'xml',
  flow: 'xml',
  flowdefinition: 'xml',
  globalvalueset: 'xml',
  group: 'xml',
  homepagecomponent: 'xml',
  homepagelayout: 'xml',
  iframewhitelisturlsettings: 'xml',
  installedpackage: 'xml',
  keywords: 'xml',
  layout: 'xml',
  letter: 'xml',
  managedcontenttype: 'xml',
  managedtopics: 'xml',
  matchingrule: 'xml',
  navigationmenu: 'xml',
  network: 'xml',
  networkbranding: 'xml',
  config: 'xml',
  permissionset: 'xml',
  permissionsetgroup: 'xml',
  profile: 'xml',
  profilepasswordpolicy: 'xml',
  profilesessionsetting: 'xml',
  quickaction: 'xml',
  remotesite: 'xml',
  reporttype: 'xml',
  role: 'xml',
  rule: 'xml',
  settings: 'xml',
  sharingrules: 'xml',
  site: 'xml',
  topicsforobjects: 'xml',
  usercriteria: 'xml',
  weblink: 'xml',
  workflow: 'xml',
};

/**
 * Resolve file icon type from metadataType and/or fileName.
 * 1. metadataType hints (apexclass, apextrigger, etc.) take precedence
 * 2. fileName extension lookup in map
 * 3. Fallback: xml (most Salesforce files are XML)
 */
export function getFileIconType(
  metadataType?: string,
  fileName?: string
): FileIconType {
  if (metadataType) {
    const type = metadataType.toLowerCase();
    if (type.includes('apexclass')) return 'apex';
    if (type.includes('apextrigger')) return 'trigger';
    if (type.includes('apexpage') || type.includes('visualforce')) return 'visualforce';
    if (type.includes('lwc') || type.includes('lightningcomponent')) {
      // LWC bundles contain .html, .css, and .js files — use extension to pick the right icon/language
      if (fileName) {
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (ext === 'html') return 'html';
        if (ext === 'css') return 'css';
      }
      return 'lwc';
    }
    if (type.includes('aura')) return 'aura';
  }

  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext && ext in EXTENSION_TO_ICON_TYPE) {
      return EXTENSION_TO_ICON_TYPE[ext];
    }
  }

  return DEFAULT_ICON_TYPE;
}

/**
 * Monaco Editor language id for syntax highlighting.
 * Monaco uses: java, xml, javascript, html, json, css, plaintext
 */
export type MonacoLanguageId = 'java' | 'xml' | 'javascript' | 'html' | 'json' | 'css' | 'plaintext';

/**
 * Map FileIconType to Monaco Editor language id.
 * Apex/Trigger use java; Visualforce uses html; most metadata uses xml.
 */
export const FILE_ICON_TYPE_TO_MONACO_LANGUAGE: Record<FileIconType, MonacoLanguageId> = {
  apex: 'java',
  trigger: 'java',
  visualforce: 'html',
  lwc: 'javascript',
  aura: 'javascript',
  xml: 'xml',
  json: 'json',
  css: 'css',
  html: 'html',
  javascript: 'javascript',
  certificate: 'plaintext',
  default: 'plaintext',
};

/** Default Monaco language for unknown types. Most Salesforce metadata is XML. */
export const DEFAULT_MONACO_LANGUAGE: MonacoLanguageId = 'xml';

/**
 * Resolve Monaco Editor language id for syntax highlighting.
 * Uses the same logic as getFileIconType, then maps to Monaco language.
 * Add one entry to EXTENSION_TO_ICON_TYPE to extend both icons and editor highlighting.
 */
export function getMonacoLanguage(
  metadataType?: string,
  fileName?: string
): MonacoLanguageId {
  const iconType = getFileIconType(metadataType, fileName);
  return FILE_ICON_TYPE_TO_MONACO_LANGUAGE[iconType] ?? DEFAULT_MONACO_LANGUAGE;
}

/**
 * Format API language (used by code formatter backend).
 * Differs from Monaco: apex/trigger use "apex" (backend supports Apex), not "java".
 */
export type FormatApiLanguage = 'apex' | 'html' | 'javascript' | 'xml' | 'json' | 'css';

/**
 * Map FileIconType to format API language.
 * apex/trigger → apex; visualforce → html; lwc/aura → javascript; most → xml.
 */
export const FILE_ICON_TYPE_TO_FORMAT_API_LANGUAGE: Partial<Record<FileIconType, FormatApiLanguage>> = {
  apex: 'apex',
  trigger: 'apex',
  visualforce: 'html',
  lwc: 'javascript',
  aura: 'javascript',
  xml: 'xml',
  json: 'json',
  css: 'css',
  html: 'html',
  javascript: 'javascript',
};

/**
 * Resolve format API language for code formatting.
 * Returns undefined for unsupported types (certificate, default).
 */
export function getFormatApiLanguage(
  metadataType?: string,
  fileName?: string
): FormatApiLanguage | undefined {
  const iconType = getFileIconType(metadataType, fileName);
  return FILE_ICON_TYPE_TO_FORMAT_API_LANGUAGE[iconType];
}
